import React, { memo, useCallback, useState } from 'react';
import Div from '../Div';
import Search from '@/components/Icons/Search';
import ShoppingCart from '@/components/Icons/ShoppingCart';
import Account from '@/components/Icons/Account';
import Menu from '@/components/Icons/Menu';
import Map from '@/components/Map';
import useScreen from '@/hooks/useScreen';
import Container from '../Container';
import Button from '../Button';
import MenuNav from './MenuNav';
import { useProfileContext } from '@/context/profileContext';
import { useAppConfigContext } from '@/context/AppContext';
import useListenWindowEvent from '@/hooks/useListenWindowEvent';
import Logo from '@/components/Icons/Logo';
import classNames from 'classnames';
import { ECOM_CART_URL, ECOM_HOME_URL, LOGIN_URL, MY_DEVICES_TAB_URL } from '@/configs/constants';
import Paragraph from '../Paragraph';
import RightArrow from '@/components/Icons/RightArrow';
import Image from '@/shared/Image';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { logout } from '@/actions/ecom/logout';
import Cookies from '@/utils/cookie';
import { SESSION_COOKIE } from '@/configs/serviceConstants';
import { deleteLogoutTokens } from '@/utils/deleteLogoutTokens';
import { useEcomProductContext } from '@/context/ecomProductContext';
import useEcomGTM from '@/hooks/useEcomGTM';
import { EcomGTMEventNames } from '@/types/ecomGTM';
import { useRouter } from 'next/router';
import { LOGIN_REDIRECT_TO_QUERY } from '@/constants/app';
import LeftArrowIcon from '@/components/Icons/LeftArrow';
import { showAQIBar } from '@/utils/aqi';

type IconData = {
  id?: number;
  Icon: JSX.Element;
  label?: string;
  url?: string;
  labelURl?: string;
};

let iconsArr: IconData[] = [
  {
    id: 1,
    Icon: <Search />,
    label: 'Search',
  },
  {
    id: 2,
    Icon: <ShoppingCart />,
    url: ECOM_CART_URL,
    label: 'Cart',
  },
  {
    id: 3,
    Icon: <Account />,
    label: 'Log in',
    url: LOGIN_URL,
  },
  {
    id: 4,
    Icon: <Menu />,
    label: 'Menu',
  },
];

function Navbar({ showTopMenu = false }: { showTopMenu?: boolean }) {
  const [show, setShow] = useState(false);
  const { state: appConfig, dispatch: appConfigDispatch } = useAppConfigContext();
  const { state: profileState } = useProfileContext();
  const { isFlutterClient, aqiBar } = appConfig;
  const { nav: navData, featured, profile } = appConfig.header ?? {};
  const { state: productContextState } = useEcomProductContext();
  const { isDesktop } = useScreen();
  const router = useRouter();
  const { fireEcomGTMEvent } = useEcomGTM();
  const scrollHeader = useCallback(() => {
    if (window.scrollY >= 130) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, []);

  useListenWindowEvent('scroll', scrollHeader, 1);

  const modalHandle = async (id: number, url: string) => {
    switch (id) {
      case 1:
        appConfigDispatch.toggleSearch();
        break;
      case 2:
        fireEcomGTMEvent(EcomGTMEventNames.HEADER.CART_ICON, {});
        window.location.href = url;
        break;
      case 4: {
        if (!isDesktop) {
          appConfigDispatch.toggleHamburger();
        }
        break;
      }
    }
  };

  const fetch = (...args: any) => logout.apply(null, args);
  const { promisifyTrigger } = useTriggerRequest({ fetch });

  const handleLogoutClick = async () => {
    await promisifyTrigger(Cookies.get(SESSION_COOKIE));

    await deleteLogoutTokens();
    window.location.href = ECOM_HOME_URL;
  };

  const renderVerticalMenu = useCallback(() => {
    return (
      <Map
        className="absolute -right-[40px] mt-s7 w-64 rounded-xl bg-white top-10 border border-co-subtle  shadow-lg opacity-0 group-hover:opacity-100 hidden group-hover:block overflow-hidden"
        list={profile}
        render={({ label, url, target, icon, id }, index) => {
          const islastItem = index == profile.length - 1;
          return (
            <Button
              theme="none"
              className="!p-s5  !flex-row  !items-cente gap-s4 w-full !text-start hover:bg-cti-on-secondary "
              key={id}
              target={target}
              href={url === '#logout' ? undefined : url}
              onClick={url === '#logout' ? handleLogoutClick : undefined}
            >
              <Image alt={label} src={icon} width={24} height={24} />
              <Paragraph className="flex-1 text-cti-primary text-body-lg">{label}</Paragraph>
              {!islastItem && <RightArrow />}
            </Button>
          );
        }}
      />
    );
  }, [profile]);

  // For Flutter client, back button handler
  const backButtonHandler = () => {
    if (router.pathname === '/ecom/order/[orderItemId]') {
      router.push('/ecom/orders');
      return;
    }

    if (router.pathname === '/ecom/orders') {
      window.location.href = '/?closeMyAccount=true';
      return;
    }

    if (window.history.length > 2) {
      router.back();
    } else router.push(ECOM_HOME_URL);
  };

  const renderLogoOrBackButton = () => {
    if (isFlutterClient && router.pathname !== ECOM_HOME_URL) {
      return (
        <Button theme="icon" onClick={backButtonHandler}>
          <LeftArrowIcon fill="#424242" />
        </Button>
      );
    }

    return (
      <Button theme="none" title="Eureka forbes" target="_self" track={false} href={ECOM_HOME_URL}>
        <Logo />
      </Button>
    );
  };

  return (
    <>
      <header className="font-primary">
        <nav
          className={classNames('fixed top-0 bg-transparent w-full z-50 font-primary', {
            'top-s8': aqiBar && showAQIBar(router?.pathname),
          })}
          data-testid={'navbar-container'}
        >
          <Div className="bg-cs-primary shadow-subtle border-b border-co-subtle w-full relative">
            <Container className="flex justify-between">
              <Div className="flex gap-s6">
                {renderLogoOrBackButton()}
                {isDesktop && (show || !showTopMenu) && (
                  <Map
                    className="flex gap-s7"
                    list={navData}
                    wrapper="div"
                    render={({ url, target = '_self', label }, index) => {
                      return (
                        <Button
                          className="text-body-lg text-cti-primary cursor-pointer whitespace-nowrap"
                          key={index}
                          theme="none"
                          href={url}
                          target={target}
                        >
                          {label}
                        </Button>
                      );
                    }}
                  />
                )}
              </Div>
              <Div className="flex gap-s6">
                {isDesktop && (
                  <Map
                    list={featured}
                    render={({ url, target = '_self', label }, index) => {
                      return (
                        <Button
                          className="text-body-lg text-cti-primary"
                          key={index}
                          theme="none"
                          href={url}
                          target={target}
                        >
                          {label}
                        </Button>
                      );
                    }}
                  />
                )}

                <Map
                  className="flex gap-s5"
                  list={iconsArr}
                  wrapper="div"
                  render={({ Icon, label, url, id }, index) => {
                    if (isFlutterClient) {
                      if (
                        router.pathname === '/ecom/orders' ||
                        router.pathname === '/ecom/order/[orderItemId]'
                      )
                        return <Div className="h-14" forceRender />;
                      if (id === 3 || id === 4) return null;
                    }

                    if (isDesktop && id == 4) return null;
                    let href = url;
                    if (id === 3) {
                      if (router.asPath !== LOGIN_URL) {
                        href = `${url}?${LOGIN_REDIRECT_TO_QUERY}=${router.asPath}`;
                      }

                      if (profileState.isLoggedIn) {
                        href = MY_DEVICES_TAB_URL;
                      }
                    }
                    return (
                      <Div
                        className={classNames('flex gap-s3 py-s5 lg:py-s6', {
                          'relative group': index == 2 || index === 1,
                        })}
                        key={index}
                      >
                        <Button
                          theme="none"
                          title={label}
                          onClick={() => modalHandle(id, href)}
                          href={id === 2 ? undefined : href}
                          track={false}
                          dataTestId={`nav-icon-${label}`}
                        >
                          <Div className="text-body-lg text-cti-primary cursor-pointer">{Icon}</Div>
                          {isDesktop && !profileState.isLoggedIn && index == 2 && (
                            <Div dataTestId="user-label">{label} </Div>
                          )}
                        </Button>
                        {index === 1 && productContextState.inCartItems.length > 0 && (
                          <Div
                            className="absolute top-0 right-0 mt-s5 lg:mt-s6 w-[8px] h-[8px] bg-red-500 rounded-full"
                            forceRender={true}
                          />
                        )}
                        {index == 2 && isDesktop && profileState.isLoggedIn && renderVerticalMenu()}
                      </Div>
                    );
                  }}
                />
              </Div>
            </Container>
          </Div>
        </nav>
      </header>
      {showTopMenu && (
        <Div
          className={classNames('flex justify-center mt-14 md:mt-18 ', {
            'mt-[97px] md:mt-[112px]': appConfig.aqiBar && showAQIBar(router?.pathname),
          })}
        >
          <Map
            className="flex px-s4 overflow-x-auto gap-s4 whitespace-nowrap hide-scrollbar leading-none"
            list={navData}
            render={({ icon, label, url, target = '_self' }: any, index: number) => {
              return (
                <MenuNav
                  image={icon}
                  label={label}
                  href={url}
                  target={target}
                  key={index}
                  dataTestId={`menu-${index}`}
                />
              );
            }}
          />
        </Div>
      )}
    </>
  );
}

export default memo(Navbar);
